import React, { useState, useEffect, useCallback } from 'react';
import { useAccount, useBalance, useWriteContract, useWaitForTransactionReceipt, useReadContract } from 'wagmi';
import { useConnectModal } from '@rainbow-me/rainbowkit';
import { formatUnits, parseUnits, parseEther } from 'viem';
import { ArrowDownUp, Loader2, Settings, Info, Wallet, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

// Contract addresses
const TIP_TOKEN = '0x45D8e90deFB7a88C3e5584043205da5a90a43A34';
const WBNB = '0xbb4CdB9CBd36B01bD1cBaEBF2De08d9173bc095c';
const PANCAKE_ROUTER = '0x10ED43C718714eb63d5aA57B78B54704E256024E';
const LP_PAIR = '0xb205dbfaf48815c06ffd1a865751eceba2f184b2';

// ABIs
const ERC20_ABI = [
  {
    name: 'balanceOf',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ name: 'account', type: 'address' }],
    outputs: [{ name: '', type: 'uint256' }],
  },
  {
    name: 'allowance',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ name: 'owner', type: 'address' }, { name: 'spender', type: 'address' }],
    outputs: [{ name: '', type: 'uint256' }],
  },
  {
    name: 'approve',
    type: 'function',
    stateMutability: 'nonpayable',
    inputs: [{ name: 'spender', type: 'address' }, { name: 'amount', type: 'uint256' }],
    outputs: [{ name: '', type: 'bool' }],
  },
  {
    name: 'decimals',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ name: '', type: 'uint8' }],
  },
  {
    name: 'symbol',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ name: '', type: 'string' }],
  },
];

const ROUTER_ABI = [
  {
    name: 'getAmountsOut',
    type: 'function',
    stateMutability: 'view',
    inputs: [
      { name: 'amountIn', type: 'uint256' },
      { name: 'path', type: 'address[]' },
    ],
    outputs: [{ name: 'amounts', type: 'uint256[]' }],
  },
  {
    name: 'swapExactTokensForETHSupportingFeeOnTransferTokens',
    type: 'function',
    stateMutability: 'nonpayable',
    inputs: [
      { name: 'amountIn', type: 'uint256' },
      { name: 'amountOutMin', type: 'uint256' },
      { name: 'path', type: 'address[]' },
      { name: 'to', type: 'address' },
      { name: 'deadline', type: 'uint256' },
    ],
    outputs: [],
  },
  {
    name: 'swapExactETHForTokensSupportingFeeOnTransferTokens',
    type: 'function',
    stateMutability: 'payable',
    inputs: [
      { name: 'amountOutMin', type: 'uint256' },
      { name: 'path', type: 'address[]' },
      { name: 'to', type: 'address' },
      { name: 'deadline', type: 'uint256' },
    ],
    outputs: [],
  },
];

// Debug flag - set to true to show debug panel
const DEBUG_WALLET = true;

// Slippage constants
const SLIPPAGE_STORAGE_KEY = 'tipme_dex_slippage_settings';
const MIN_SLIPPAGE = 0.1;
const MAX_SLIPPAGE = 20.0;
const DEFAULT_AUTO_SLIPPAGE = 10; // Base auto slippage for TIP token (due to tax)
const TIP_TOKEN_TAX = 5; // TIP has a 5% redistribution tax

// Load slippage settings from localStorage
const loadSlippageSettings = () => {
  try {
    const saved = localStorage.getItem(SLIPPAGE_STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      return {
        isAuto: parsed.isAuto !== false, // default true
        manualValue: parsed.manualValue || DEFAULT_AUTO_SLIPPAGE,
      };
    }
  } catch (e) {
    console.error('Failed to load slippage settings:', e);
  }
  return { isAuto: true, manualValue: DEFAULT_AUTO_SLIPPAGE };
};

// Save slippage settings to localStorage
const saveSlippageSettings = (settings) => {
  try {
    localStorage.setItem(SLIPPAGE_STORAGE_KEY, JSON.stringify(settings));
  } catch (e) {
    console.error('Failed to save slippage settings:', e);
  }
};

// Calculate auto slippage based on trade size and liquidity
const calculateAutoSlippage = (fromToken, fromAmount, quoteData) => {
  // Base slippage accounts for TIP token tax
  let baseSlippage = TIP_TOKEN_TAX;
  
  // Add buffer for price movement
  const priceBuffer = 0.5;
  
  // Estimate price impact based on trade size (simplified)
  // In production, this would use actual LP reserves
  let priceImpact = 0;
  if (fromAmount && parseFloat(fromAmount) > 0) {
    const amount = parseFloat(fromAmount);
    if (fromToken === 'BNB') {
      // Larger BNB trades have more impact
      if (amount > 1) priceImpact = 1;
      else if (amount > 0.5) priceImpact = 0.5;
      else if (amount > 0.1) priceImpact = 0.3;
    } else {
      // TIP to BNB - estimate based on TIP amount
      if (amount > 10000) priceImpact = 1.5;
      else if (amount > 5000) priceImpact = 1;
      else if (amount > 1000) priceImpact = 0.5;
    }
  }
  
  // Total recommended slippage
  const recommended = baseSlippage + priceBuffer + priceImpact;
  
  // Round to 1 decimal and ensure within bounds
  return Math.min(Math.max(Math.round(recommended * 10) / 10, MIN_SLIPPAGE), MAX_SLIPPAGE);
};

// Helper to gather wallet provider debug info
const getProviderDebugInfo = () => {
  if (typeof window === 'undefined') {
    return { error: 'window undefined (SSR)' };
  }
  
  const info = {
    userAgent: navigator.userAgent,
    inIframe: window.self !== window.top,
    hasEthereum: !!window.ethereum,
    ethereumFlags: {},
    providers: [],
    chainId: null,
    selectedAddress: null,
  };
  
  if (window.ethereum) {
    const eth = window.ethereum;
    info.ethereumFlags = {
      isMetaMask: !!eth.isMetaMask,
      isRabby: !!eth.isRabby,
      isTrust: !!eth.isTrust,
      isCoinbaseWallet: !!eth.isCoinbaseWallet,
      isBraveWallet: !!eth.isBraveWallet,
      isTokenPocket: !!eth.isTokenPocket,
    };
    info.chainId = eth.chainId;
    info.selectedAddress = eth.selectedAddress;
    
    // Check for multiple providers
    if (eth.providers && Array.isArray(eth.providers)) {
      info.providers = eth.providers.map((p, i) => ({
        index: i,
        isMetaMask: !!p.isMetaMask,
        isRabby: !!p.isRabby,
        isTrust: !!p.isTrust,
      }));
    }
  }
  
  // Check for Rabby-specific global
  if (window.rabby) {
    info.hasRabbyGlobal = true;
  }
  
  // Check for Trust-specific global
  if (window.trustwallet) {
    info.hasTrustGlobal = true;
  }
  
  return info;
};

export const EmbedSwapPage = () => {
  const { address, isConnected } = useAccount();
  const { openConnectModal } = useConnectModal();
  
  // State
  const [fromToken, setFromToken] = useState('BNB');
  const [toToken, setToToken] = useState('TIP');
  const [fromAmount, setFromAmount] = useState('');
  const [toAmount, setToAmount] = useState('');
  
  // Slippage state - loaded from localStorage
  const [slippageSettings, setSlippageSettings] = useState(() => loadSlippageSettings());
  const [manualSlippageInput, setManualSlippageInput] = useState(slippageSettings.manualValue.toString());
  const [slippageError, setSlippageError] = useState('');
  
  const [showSettings, setShowSettings] = useState(false);
  const [loading, setLoading] = useState(false);
  const [needsApproval, setNeedsApproval] = useState(false);
  const [showDebug, setShowDebug] = useState(false);
  const [debugInfo, setDebugInfo] = useState(null);
  
  // Gather debug info on mount and when connection changes
  useEffect(() => {
    if (DEBUG_WALLET) {
      setDebugInfo(getProviderDebugInfo());
    }
  }, [isConnected]);
  
  // Compute effective slippage
  // Handle slippage mode toggle
  const handleSlippageModeChange = (isAuto) => {
    const newSettings = { ...slippageSettings, isAuto };
    setSlippageSettings(newSettings);
    saveSlippageSettings(newSettings);
  };
  
  // Handle manual slippage input
  const handleManualSlippageChange = (value) => {
    setManualSlippageInput(value);
    
    const numValue = parseFloat(value);
    if (isNaN(numValue)) {
      setSlippageError('Enter a valid number');
      return;
    }
    if (numValue < MIN_SLIPPAGE) {
      setSlippageError(`Minimum ${MIN_SLIPPAGE}%`);
      return;
    }
    if (numValue > MAX_SLIPPAGE) {
      setSlippageError(`Maximum ${MAX_SLIPPAGE}%`);
      return;
    }
    
    setSlippageError('');
    const newSettings = { ...slippageSettings, manualValue: numValue };
    setSlippageSettings(newSettings);
    saveSlippageSettings(newSettings);
  };
  
  // Preset slippage values for quick selection
  const slippagePresets = [6, 8, 10, 12];
  
  // Get BNB balance
  const { data: bnbBalance, refetch: refetchBnb } = useBalance({
    address: address,
  });
  
  // Get TIP balance
  const { data: tipBalance, refetch: refetchTip } = useReadContract({
    address: TIP_TOKEN,
    abi: ERC20_ABI,
    functionName: 'balanceOf',
    args: [address],
    enabled: !!address,
  });
  
  // Get TIP allowance
  const { data: tipAllowance, refetch: refetchAllowance } = useReadContract({
    address: TIP_TOKEN,
    abi: ERC20_ABI,
    functionName: 'allowance',
    args: [address, PANCAKE_ROUTER],
    enabled: !!address,
  });
  
  // Get quote
  const { data: quoteData } = useReadContract({
    address: PANCAKE_ROUTER,
    abi: ROUTER_ABI,
    functionName: 'getAmountsOut',
    args: fromAmount && parseFloat(fromAmount) > 0 ? [
      fromToken === 'BNB' 
        ? parseEther(fromAmount || '0')
        : parseUnits(fromAmount || '0', 18),
      fromToken === 'BNB' ? [WBNB, TIP_TOKEN] : [TIP_TOKEN, WBNB],
    ] : undefined,
    enabled: !!fromAmount && parseFloat(fromAmount) > 0,
  });
  
  // Compute effective slippage (must be after quoteData is declared)
  const autoSlippage = calculateAutoSlippage(fromToken, fromAmount, quoteData);
  const effectiveSlippage = slippageSettings.isAuto ? autoSlippage : slippageSettings.manualValue;
  
  // Write contracts
  const { writeContract: approve, data: approveHash, isPending: isApproving } = useWriteContract();
  const { writeContract: swap, data: swapHash, isPending: isSwapping } = useWriteContract();
  
  // Wait for transactions
  const { isLoading: isApproveConfirming, isSuccess: isApproveSuccess } = useWaitForTransactionReceipt({
    hash: approveHash,
  });
  
  const { isLoading: isSwapConfirming, isSuccess: isSwapSuccess } = useWaitForTransactionReceipt({
    hash: swapHash,
  });
  
  // Update quote when input changes
  useEffect(() => {
    if (quoteData && quoteData[1]) {
      const outputAmount = formatUnits(quoteData[1], 18);
      setToAmount(parseFloat(outputAmount).toFixed(6));
    } else if (!fromAmount || parseFloat(fromAmount) === 0) {
      setToAmount('');
    }
  }, [quoteData, fromAmount]);
  
  // Check if approval needed
  useEffect(() => {
    if (fromToken === 'TIP' && fromAmount && tipAllowance !== undefined) {
      const amountNeeded = parseUnits(fromAmount || '0', 18);
      setNeedsApproval(tipAllowance < amountNeeded);
    } else {
      setNeedsApproval(false);
    }
  }, [fromToken, fromAmount, tipAllowance]);
  
  // Handle successful transactions
  useEffect(() => {
    if (isApproveSuccess) {
      toast.success('Approval successful!');
      refetchAllowance();
    }
  }, [isApproveSuccess]);
  
  useEffect(() => {
    if (isSwapSuccess) {
      toast.success('Swap successful!');
      setFromAmount('');
      setToAmount('');
      refetchBnb();
      refetchTip();
    }
  }, [isSwapSuccess]);
  
  // Switch tokens
  const handleSwitch = () => {
    setFromToken(toToken);
    setToToken(fromToken);
    setFromAmount(toAmount);
    setToAmount(fromAmount);
  };
  
  // Handle max
  const handleMax = () => {
    if (fromToken === 'BNB' && bnbBalance) {
      // Leave some BNB for gas
      const maxBnb = Math.max(0, parseFloat(formatUnits(bnbBalance.value, 18)) - 0.01);
      setFromAmount(maxBnb.toFixed(6));
    } else if (fromToken === 'TIP' && tipBalance) {
      setFromAmount(formatUnits(tipBalance, 18));
    }
  };
  
  // Handle approve
  const handleApprove = async () => {
    try {
      approve({
        address: TIP_TOKEN,
        abi: ERC20_ABI,
        functionName: 'approve',
        args: [PANCAKE_ROUTER, parseUnits('999999999999', 18)],
      });
    } catch (error) {
      console.error('Approve error:', error);
      toast.error('Approval failed');
    }
  };
  
  // Handle swap
  const handleSwap = async () => {
    if (!fromAmount || parseFloat(fromAmount) === 0) {
      toast.error('Enter an amount');
      return;
    }
    
    // Validate slippage before swap
    if (slippageError) {
      toast.error('Please fix slippage setting');
      return;
    }
    
    try {
      const deadline = Math.floor(Date.now() / 1000) + 60 * 20; // 20 minutes
      const minOut = parseUnits(
        (parseFloat(toAmount) * (1 - effectiveSlippage / 100)).toFixed(18),
        18
      );
      
      if (fromToken === 'BNB') {
        // BNB -> TIP
        swap({
          address: PANCAKE_ROUTER,
          abi: ROUTER_ABI,
          functionName: 'swapExactETHForTokensSupportingFeeOnTransferTokens',
          args: [minOut, [WBNB, TIP_TOKEN], address, deadline],
          value: parseEther(fromAmount),
        });
      } else {
        // TIP -> BNB
        swap({
          address: PANCAKE_ROUTER,
          abi: ROUTER_ABI,
          functionName: 'swapExactTokensForETHSupportingFeeOnTransferTokens',
          args: [parseUnits(fromAmount, 18), minOut, [TIP_TOKEN, WBNB], address, deadline],
        });
      }
    } catch (error) {
      console.error('Swap error:', error);
      toast.error('Swap failed');
    }
  };
  
  const formatBalance = (balance, decimals = 18) => {
    if (!balance) return '0';
    return parseFloat(formatUnits(balance, decimals)).toFixed(4);
  };
  
  const isPending = isApproving || isApproveConfirming || isSwapping || isSwapConfirming;

  return (
    <div className="min-h-screen bg-[#c8c3bb] p-6">
      <div className="max-w-md mx-auto">
        
        {/* Header */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 bg-white/80 rounded-full px-3 py-1.5 mb-3">
            <span className="w-2 h-2 bg-[#f0b90b] rounded-full"></span>
            <span className="text-xs font-medium text-[#333]">SWAP</span>
          </div>
          <h1 className="text-2xl font-bold text-[#1a1a1a] mb-1">Swap TIP</h1>
          <p className="text-[#666] text-sm">Trade TIP tokens instantly on PancakeSwap</p>
        </div>
        
        {/* Swap Card */}
        <Card className="p-5 bg-[#e8e3db] border-0 shadow-lg rounded-2xl">
          
          {/* Settings */}
          <div className="flex justify-between items-center mb-4">
            <span className="text-sm font-medium text-[#1a1a1a]">Swap</span>
            <button 
              onClick={() => setShowSettings(!showSettings)}
              className={`p-2 rounded-lg transition-colors ${showSettings ? 'bg-[#f0b90b]/20' : 'hover:bg-[#d4cfc4]'}`}
              data-testid="slippage-settings-btn"
            >
              <Settings className={`w-4 h-4 ${showSettings ? 'text-[#f0b90b]' : 'text-[#666]'}`} />
            </button>
          </div>
          
          {/* Slippage Settings Panel */}
          {showSettings && (
            <div className="mb-4 p-4 bg-[#d4cfc4] rounded-xl space-y-4" data-testid="slippage-settings-panel">
              {/* Mode Toggle */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-[#1a1a1a]">Slippage Tolerance</span>
                  <span className="text-sm font-bold text-[#f0b90b]" data-testid="effective-slippage">
                    {effectiveSlippage}%
                  </span>
                </div>
                
                <div className="flex gap-2 mb-3">
                  <button
                    onClick={() => handleSlippageModeChange(true)}
                    className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                      slippageSettings.isAuto 
                        ? 'bg-[#f0b90b] text-white' 
                        : 'bg-white text-[#666] hover:bg-white/80'
                    }`}
                    data-testid="slippage-auto-btn"
                  >
                    Auto ({autoSlippage}%)
                  </button>
                  <button
                    onClick={() => handleSlippageModeChange(false)}
                    className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                      !slippageSettings.isAuto 
                        ? 'bg-[#f0b90b] text-white' 
                        : 'bg-white text-[#666] hover:bg-white/80'
                    }`}
                    data-testid="slippage-manual-btn"
                  >
                    Manual
                  </button>
                </div>
              </div>
              
              {/* Manual Slippage Input */}
              {!slippageSettings.isAuto && (
                <div>
                  <div className="flex gap-2 mb-2">
                    {slippagePresets.map((preset) => (
                      <button
                        key={preset}
                        onClick={() => handleManualSlippageChange(preset.toString())}
                        className={`flex-1 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                          slippageSettings.manualValue === preset
                            ? 'bg-[#f0b90b] text-white'
                            : 'bg-white text-[#666] hover:bg-white/80'
                        }`}
                        data-testid={`slippage-preset-${preset}`}
                      >
                        {preset}%
                      </button>
                    ))}
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      value={manualSlippageInput}
                      onChange={(e) => handleManualSlippageChange(e.target.value)}
                      min={MIN_SLIPPAGE}
                      max={MAX_SLIPPAGE}
                      step="0.1"
                      placeholder="Custom"
                      className={`flex-1 bg-white border-0 text-sm font-medium ${slippageError ? 'ring-2 ring-red-400' : ''}`}
                      data-testid="slippage-manual-input"
                    />
                    <span className="text-sm font-medium text-[#666]">%</span>
                  </div>
                  
                  {slippageError && (
                    <p className="text-xs text-red-500 mt-1" data-testid="slippage-error">{slippageError}</p>
                  )}
                </div>
              )}
              
              {/* Info Note */}
              <div className="pt-2 border-t border-[#b8b3ab]">
                <p className="text-xs text-[#888]">
                  <strong>Note:</strong> TIP has a {TIP_TOKEN_TAX}% redistribution tax. 
                  {slippageSettings.isAuto 
                    ? ' Auto mode calculates optimal slippage based on trade size.' 
                    : ' Higher slippage increases success rate but may result in worse execution.'}
                </p>
              </div>
            </div>
          )}
          
          {/* From Token */}
          <div className="bg-[#d4cfc4] rounded-xl p-4 mb-2">
            <div className="flex justify-between mb-2">
              <span className="text-xs text-[#888]">From</span>
              {isConnected && (
                <span className="text-xs text-[#888]">
                  Balance: {fromToken === 'BNB' 
                    ? formatBalance(bnbBalance?.value) 
                    : formatBalance(tipBalance)
                  } {fromToken}
                </span>
              )}
            </div>
            <div className="flex items-center gap-3">
              <Input
                type="number"
                placeholder="0.0"
                value={fromAmount}
                onChange={(e) => setFromAmount(e.target.value)}
                className="flex-1 bg-transparent border-0 text-2xl font-bold text-[#1a1a1a] p-0 focus-visible:ring-0"
              />
              <div className="flex items-center gap-2">
                {isConnected && (
                  <button 
                    onClick={handleMax}
                    className="text-xs text-[#f0b90b] font-medium hover:underline"
                  >
                    MAX
                  </button>
                )}
                <div className="flex items-center gap-2 bg-[#1a1a1a] text-white px-3 py-2 rounded-xl">
                  {fromToken === 'BNB' ? (
                    <div className="w-6 h-6 rounded-full bg-[#f0b90b] flex items-center justify-center">
                      <span className="text-xs font-bold">B</span>
                    </div>
                  ) : (
                    <img src="/tip-logo.png" alt="TIP" className="w-6 h-6 rounded-full" />
                  )}
                  <span className="font-medium">{fromToken}</span>
                </div>
              </div>
            </div>
          </div>
          
          {/* Switch Button */}
          <div className="flex justify-center -my-2 relative z-10">
            <button 
              onClick={handleSwitch}
              className="w-10 h-10 bg-[#f0b90b] hover:bg-[#d4a50a] rounded-xl flex items-center justify-center shadow-md transition-colors"
            >
              <ArrowDownUp className="w-5 h-5 text-white" />
            </button>
          </div>
          
          {/* To Token */}
          <div className="bg-[#d4cfc4] rounded-xl p-4 mt-2">
            <div className="flex justify-between mb-2">
              <span className="text-xs text-[#888]">To (estimated)</span>
              {isConnected && (
                <span className="text-xs text-[#888]">
                  Balance: {toToken === 'BNB' 
                    ? formatBalance(bnbBalance?.value) 
                    : formatBalance(tipBalance)
                  } {toToken}
                </span>
              )}
            </div>
            <div className="flex items-center gap-3">
              <Input
                type="number"
                placeholder="0.0"
                value={toAmount}
                readOnly
                className="flex-1 bg-transparent border-0 text-2xl font-bold text-[#1a1a1a] p-0 focus-visible:ring-0"
              />
              <div className="flex items-center gap-2 bg-[#1a1a1a] text-white px-3 py-2 rounded-xl">
                {toToken === 'BNB' ? (
                  <div className="w-6 h-6 rounded-full bg-[#f0b90b] flex items-center justify-center">
                    <span className="text-xs font-bold">B</span>
                  </div>
                ) : (
                  <img src="/tip-logo.png" alt="TIP" className="w-6 h-6 rounded-full" />
                )}
                <span className="font-medium">{toToken}</span>
              </div>
            </div>
          </div>
          
          {/* Price Info */}
          {fromAmount && toAmount && parseFloat(fromAmount) > 0 && (
            <div className="mt-4 p-3 bg-[#d4cfc4] rounded-xl">
              <div className="flex justify-between text-xs">
                <span className="text-[#888]">Rate</span>
                <span className="text-[#1a1a1a] font-medium">
                  1 {fromToken} = {(parseFloat(toAmount) / parseFloat(fromAmount)).toFixed(4)} {toToken}
                </span>
              </div>
              <div className="flex justify-between text-xs mt-1">
                <span className="text-[#888]">Slippage</span>
                <span className="text-[#1a1a1a] font-medium">
                  {effectiveSlippage}% {slippageSettings.isAuto && <span className="text-[#888]">(auto)</span>}
                </span>
              </div>
              <div className="flex justify-between text-xs mt-1">
                <span className="text-[#888]">Min. received</span>
                <span className="text-[#1a1a1a] font-medium">
                  {(parseFloat(toAmount) * (1 - effectiveSlippage / 100)).toFixed(4)} {toToken}
                </span>
              </div>
            </div>
          )}
          
          {/* Action Button */}
          <div className="mt-4">
            {!isConnected ? (
              <Button 
                onClick={openConnectModal}
                className="w-full h-12 bg-[#f0b90b] hover:bg-[#d4a50a] text-white font-semibold rounded-xl"
              >
                <Wallet className="w-5 h-5 mr-2" />
                Connect Wallet
              </Button>
            ) : needsApproval ? (
              <Button 
                onClick={handleApprove}
                disabled={isPending}
                className="w-full h-12 bg-[#f0b90b] hover:bg-[#d4a50a] text-white font-semibold rounded-xl"
              >
                {isApproving || isApproveConfirming ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Approving...
                  </>
                ) : (
                  'Approve TIP'
                )}
              </Button>
            ) : (
              <Button 
                onClick={handleSwap}
                disabled={isPending || !fromAmount || parseFloat(fromAmount) === 0}
                className="w-full h-12 bg-[#f0b90b] hover:bg-[#d4a50a] text-white font-semibold rounded-xl disabled:opacity-50"
              >
                {isSwapping || isSwapConfirming ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Swapping...
                  </>
                ) : (
                  'Swap'
                )}
              </Button>
            )}
          </div>
        </Card>
        
        {/* Info */}
        <div className="mt-4 flex items-start gap-2 text-xs text-[#888]">
          <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <p>
            Swaps are executed via PancakeSwap. A small fee may apply. 
            Always verify the transaction details in your wallet.
          </p>
        </div>
        
        {/* Debug Panel - only shown when DEBUG_WALLET is true */}
        {DEBUG_WALLET && (
          <div className="mt-4">
            <button
              onClick={() => {
                setDebugInfo(getProviderDebugInfo());
                setShowDebug(!showDebug);
              }}
              className="text-xs text-[#666] underline"
            >
              {showDebug ? 'Hide' : 'Show'} Debug Info
            </button>
            
            {showDebug && debugInfo && (
              <div className="mt-2 p-3 bg-[#1a1a1a] text-[#0f0] rounded-lg text-xs font-mono overflow-auto max-h-64">
                <div><strong>== WALLET DEBUG ==</strong></div>
                <div>Connected: {isConnected ? 'YES' : 'NO'}</div>
                <div>Address: {address || 'none'}</div>
                <div>---</div>
                <div>In Iframe: {debugInfo.inIframe ? 'YES' : 'NO'}</div>
                <div>Has window.ethereum: {debugInfo.hasEthereum ? 'YES' : 'NO'}</div>
                <div>---</div>
                <div><strong>Provider Flags:</strong></div>
                {debugInfo.ethereumFlags && Object.entries(debugInfo.ethereumFlags).map(([k, v]) => (
                  <div key={k}>&nbsp;&nbsp;{k}: {v ? 'true' : 'false'}</div>
                ))}
                <div>---</div>
                <div>ChainId: {debugInfo.chainId || 'null'}</div>
                <div>Selected Address: {debugInfo.selectedAddress || 'null'}</div>
                <div>---</div>
                <div>Has window.rabby: {debugInfo.hasRabbyGlobal ? 'YES' : 'NO'}</div>
                <div>Has window.trustwallet: {debugInfo.hasTrustGlobal ? 'YES' : 'NO'}</div>
                <div>---</div>
                <div><strong>Multiple Providers:</strong> {debugInfo.providers?.length || 0}</div>
                {debugInfo.providers?.map((p, i) => (
                  <div key={i}>&nbsp;&nbsp;[{i}] MM:{p.isMetaMask ? 'Y' : 'N'} Rabby:{p.isRabby ? 'Y' : 'N'} Trust:{p.isTrust ? 'Y' : 'N'}</div>
                ))}
                <div>---</div>
                <div className="break-all"><strong>UserAgent:</strong> {debugInfo.userAgent}</div>
              </div>
            )}
          </div>
        )}
        
      </div>
    </div>
  );
};
